<template>
  <b-container fluid>
    <b-modal v-model="displayDeletion" title="Confirm Deletion" @ok="onConfirmDeletion">
      Are you sure you want to delete this item?
    </b-modal>

    <b-modal id="map-select-modal" size="xl" v-model="displayMapModal" @shown="onMapModalShown"
      @hidden="onMapModalHidden" hide-footer title="Select Landmark from Map">
      <div id="landmark-selector-map" class="landmark-selector-map"></div>
      <div class="text-right mt-3">
        <b-button variant="secondary" @click="displayMapModal = false" class="mr-2">Cancel</b-button>
        <b-button variant="primary" @click="onLandmarkSelectedFromMap"
          :disabled="!selectedMapLandmark">Select</b-button>
      </div>
    </b-modal>

    <b-modal size="lg" hide-footer no-close-on-esc no-close-on-backdrop v-model="displayForm">
      <template #modal-title>{{ currentItem.mvygId ? 'Edit' : 'Create' }} Voyage</template>
      <b-form @submit.prevent="onSubmit">
        <b-form-group label="Asset">
          <b-form-select v-model="currentItem.mvygMassId" :options="assets" value-field="massId" text-field="massName"
            required :state="errors.mvygMassId ? false : null"></b-form-select>
          <b-form-invalid-feedback>
            {{ errors.mvygMassId }}
          </b-form-invalid-feedback>
        </b-form-group>
        <b-form-group label="Voyage Name">
          <b-form-input v-model="currentItem.mvygName" required :state="errors.mvygName ? false : null"></b-form-input>
          <b-form-invalid-feedback>
            {{ errors.mvygName }}
          </b-form-invalid-feedback>
        </b-form-group>
        <b-form-group label="Voyage No.">
          <b-form-input v-model="currentItem.mvygNumber" required
            :state="errors.mvygNumber ? false : null"></b-form-input>
          <b-form-invalid-feedback>
            {{ errors.mvygNumber }}
          </b-form-invalid-feedback>
        </b-form-group>
        <b-form-group label="Start Time">
          <b-form-input type="datetime-local" v-model="currentItem.mvygStartTime" required
            :state="errors.mvygStartTime ? false : null"></b-form-input>
          <b-form-invalid-feedback>
            {{ errors.mvygStartTime }}
          </b-form-invalid-feedback>
        </b-form-group>
        <b-form-group label="End Time">
          <b-form-input type="datetime-local" v-model="currentItem.mvygEndTime" required
            :state="errors.mvygEndTime ? false : null"></b-form-input>
          <b-form-invalid-feedback>
            {{ errors.mvygEndTime }}
          </b-form-invalid-feedback>
        </b-form-group>
        <b-form-group label="Origin">
          <LandmarkSearchInput
            ref="originInput"
            v-model="currentItem.mvygOriginLndmId"
            :landmarks="landmarks"
            label="Origin"
            :state="errors.mvygOriginLndmId ? false : null"
            @select-on-map="openMapSelector('origin')"
          />
          <b-form-invalid-feedback :state="errors.mvygOriginLndmId ? false : null">
            {{ errors.mvygOriginLndmId }}
          </b-form-invalid-feedback>
        </b-form-group>
        <b-form-group label="Destination">
          <LandmarkSearchInput
            ref="destinationInput"
            v-model="currentItem.mvygDestinationLndmId"
            :landmarks="landmarks"
            label="Destination"
            :state="errors.mvygDestinationLndmId ? false : null"
            @select-on-map="openMapSelector('destination')"
          />
          <b-form-invalid-feedback :state="errors.mvygDestinationLndmId ? false : null">
            {{ errors.mvygDestinationLndmId }}
          </b-form-invalid-feedback>
        </b-form-group>
        <b-form-group label="Cargo Status">
          <b-form-select v-model="currentItem.mvygCargoStatus" :options="cargoStatusOptions" required
            :state="errors.mvygCargoStatus ? false : null"></b-form-select>
          <b-form-invalid-feedback>
            {{ errors.mvygCargoStatus }}
          </b-form-invalid-feedback>
        </b-form-group>
        <b-form-group v-show="currentItem.mvygCargoStatus" label="Cargo Load (Kg)">
          <b-form-input type="number" v-model="currentItem.mvygCargoLoadKg"
            :state="errors.mvygCargoLoadKg ? false : null"></b-form-input>
          <b-form-invalid-feedback>
            {{ errors.mvygCargoLoadKg }}
          </b-form-invalid-feedback>
        </b-form-group>
        <b-form-group v-show="currentItem.mvygCargoStatus" label="Cargo Type">
          <b-form-input v-model="currentItem.mvygCargoType" :state="errors.mvygCargoType ? false : null"></b-form-input>
          <b-form-invalid-feedback>
            {{ errors.mvygCargoType }}
          </b-form-invalid-feedback>
        </b-form-group>
        <b-form-group label="Note">
          <b-form-textarea v-model="currentItem.mvygNote" :state="errors.mvygNote ? false : null"></b-form-textarea>
          <b-form-invalid-feedback>
            {{ errors.mvygNote }}
          </b-form-invalid-feedback>
        </b-form-group>
        <div class="text-right">
          <b-button type="button" variant="secondary" @click="onCancel" class="mr-2">Cancel</b-button>
          <b-button type="submit" variant="primary" :disabled="isSubmit">
            <b-spinner small v-if="isSubmit"></b-spinner>
            Save
          </b-button>
        </div>
      </b-form>
    </b-modal>
    <div class="iq-card mt-3 px-3">
      <div class="iq-card-header d-flex justify-content-between">
        <div class="iq-header-title py-3">
          <h4 class="card-title">Voyage</h4>
        </div>
        <div class="text-right">
          <button-options :access="'voyage_master'" :hideAdd="false" :hideDetail="true" :isRefreshEnabled="true"
            :isRefreshed="isRefreshed" @onRefreshed="getData()" @onAddItemClicked="onAdd" class="mt-2" />
        </div>
      </div>
      <div class="iq-card-body">
        <b-row class="mb-4">
          <b-col md="4">
            <b-form-group label="Asset" label-for="asset-select">
              <b-form-select id="asset-select" v-model="selectedAsset" :options="assets" value-field="massId"
                text-field="massName" @change="onAssetSelected">
                <template #first>
                  <b-form-select-option :value="null" disabled>-- Please select an asset --</b-form-select-option>
                </template>
              </b-form-select>
            </b-form-group>
          </b-col>
        </b-row>
        <b-table striped hover fixed head-variant="dark" show-empty responsive :busy="isLoading" :fields="fields"
          :items="data" style="border-radius: 10px;">
          <template v-slot:table-busy="">
            <div class="text-center text-danger my-2">
              <b-spinner variant="success" size="sm" class="align-middle"></b-spinner>
            </div>
          </template>
          <template v-slot:empty>
            <div class="text-center text-muted my-2">
              <strong>No data to show.</strong>
            </div>
          </template>
          <template #cell(actions)="row">
            <button-options access="voyage_master" :hideDetail="false" :hideDuplicate="true" :hideDelete="false"
              :hideEdit="false" :isRefreshEnabled="false" @onViewClicked="onDetail(row.item)"
              @onEditClicked="onEdit(row.item)" @onDeleteClicked="onDelete(row.item)" />
          </template>
        </b-table>
        <b-pagination v-if="data && data.length > 0" v-model="pagination.current" :per-page="pagination.pageSize"
          :total-rows="pagination.totalSize" @change="onPageChanged"></b-pagination>
      </div>
    </div>
  </b-container>
</template>

<script>
/* eslint-disable no-undef, new-cap */
import ButtonOptions from '@/components/directives/button-options';
import VoyageMasterSvc from '@/services/VoyageMasterSvc';
import AssetSvc from '@/services/AssetSvc';
import LandmarkSvc from '@/services/LandmarkSvc';
import requestBuilder from '@/util/request.builder';
import moment from 'moment';
import LandmarkSearchInput from '@/views/voyage/components/LandmarkSearchInput.vue';

export default {
  name: 'Voyage',
  components: {
    ButtonOptions,
    LandmarkSearchInput,
  },
  data() {
    return {
      voyageMaster: new VoyageMasterSvc(),
      data: [],
      isLoading: false,
      isRefreshed: false,
      displayDeletion: false,
      selectedItem: null,
      displayForm: false,
      isSubmit: false,
      errors: {},
      currentItem: {},
      assets: [],
      selectedAsset: null,
      assetsLoaded: false,
      landmarks: [],
      displayMapModal: false,
      landmarkSelectionTarget: null,
      mapInstance: null,
      markerLayer: null,
      mapLandmarks: [],
      selectedMapLandmark: null,
      mapLoading: false,
      mapInitInProgress: false,
      cargoStatusOptions: [
        { value: 0, text: 'No Cargo' },
        { value: 1, text: 'With Cargo' },
      ],
      pagination: {
        current: 1,
        pageSize: 20,
        totalSize: 0,
      },
      fields: [
        { key: 'mvygName', label: 'Name' },
        { key: 'mvygNumber', label: 'Number' },
        {
          key: 'mvygStartTime',
          label: 'Start Time',
          formatter: (value) => moment(value).format('DD-MM-YYYY HH:mm'),
        },
        {
          key: 'mvygEndTime',
          label: 'End Time',
          formatter: (value) => moment(value).format('DD-MM-YYYY HH:mm'),
        },
        {
          key: 'mvygCargoStatus',
          label: 'Cargo Status',
          formatter: (value) => (value ? 'With Cargo' : 'No Cargo'),
        },
        { key: 'mvygCargoLoadKg', label: 'Cargo Load (Kg)' },
        { key: 'mvygCargoType', label: 'Cargo Type' },
        { key: 'actions', label: 'Actions', class: 'text-center' },
      ],
    };
  },
  methods: {
    async getLandmarks() {
      try {
        const compId = this.$store.getters['auth/activeCompany'].compId;
        const [companyLandmarksRes, globalLandmarksRes] = await Promise.all([
          LandmarkSvc.landmarks.get({ params: { compId, max: 1000 } }),
          LandmarkSvc.landmarks.get({ params: { max: 1000 } }),
        ]);

        const allLandmarks = new Map();

        (globalLandmarksRes.data.data || []).forEach((l) => {
          allLandmarks.set(l.lndmId, { ...l, displayName: `[GLOBAL] ${l.lndmName}` });
        });

        (companyLandmarksRes.data.data || []).forEach((l) => {
          allLandmarks.set(l.lndmId, { ...l, displayName: `[COMPANY] ${l.lndmName}` });
        });

        this.landmarks = Array.from(allLandmarks.values());
      } catch (e) {
        console.error('get landmarks error : ', e);
        this.$toast.error('Failed to load landmarks');
      }
    },
    async getAssets() {
      try {
        const { data } = await AssetSvc.getAssets();
        this.assets = data.data;

        // Validate and set selected asset after assets are loaded
        const savedAssetId = sessionStorage.getItem('voyage_selected_asset');
        if (savedAssetId) {
          // Convert to number if massId is numeric
          const savedAssetIdNum = Number(savedAssetId);
          const isValid = this.assets.some((a) => a.massId === savedAssetIdNum);
          if (isValid) {
            this.selectedAsset = savedAssetIdNum;
          } else {
            this.selectedAsset = this.assets.length > 0 ? this.assets[0].massId : null;
          }
        } else {
          this.selectedAsset = this.assets.length > 0 ? this.assets[0].massId : null;
        }

        // Mark assets as loaded and trigger data fetch
        this.assetsLoaded = true;
        if (this.selectedAsset) {
          this.getData();
        }
      } catch (e) {
        console.error('get assets error : ', e);
        this.$toast.error('Failed to load assets');
      }
    },
    async getData() {
      if (!this.selectedAsset) {
        this.data = [];
        this.pagination.totalSize = 0;
        return;
      }
      try {
        this.isLoading = true;
        this.isRefreshed = true;
        const filters = {
          mvygMassId: this.selectedAsset,
        };
        const Response = await this.voyageMaster.getAll({
          ...requestBuilder.buildFilters(this.pagination, filters),
        });

        const Data = Response.data;
        this.pagination.totalSize = Response.pagination.totalData;
        this.data = Data;
      } catch (e) {
        console.error('get data error : ', e);
        this.$toast.error('Something Wrong');
      } finally {
        this.isLoading = false;
        this.isRefreshed = false;
      }
    },
    onAssetSelected() {
      this.pagination.current = 1;
      sessionStorage.setItem('voyage_selected_asset', this.selectedAsset);
      // getData() will be called by the watcher
    },
    onPageChanged(page) {
      this.pagination.current = page;
      this.getData();
    },
    onAdd() {
      if (!this.selectedAsset) {
        this.$toast.error('Please select an asset first');
        return;
      }
      this.errors = {};
      this.currentItem = {
        mvygName: '',
        mvygNumber: '',
        mvygStartTime: moment().format('YYYY-MM-DDTHH:mm'),
        mvygEndTime: moment().add(1, 'day').format('YYYY-MM-DDTHH:mm'),
        mvygOriginLndmId: null,
        originDisplayName: '',
        mvygDestinationLndmId: null,
        destinationDisplayName: '',
        mvygCargoStatus: 0,
        mvygCargoLoadKg: 0,
        mvygCargoType: '-',
        mvygNote: '',
        mvygMassId: this.selectedAsset,
      };
      this.displayForm = true;
    },
    onEdit(item) {
      this.errors = {};
      const origin = this.landmarks.find((l) => l.lndmId === item.mvygOriginLndmId);
      const destination = this.landmarks.find((l) => l.lndmId === item.mvygDestinationLndmId);
      this.currentItem = {
        ...item,
        mvygStartTime: moment(item.mvygStartTime).format('YYYY-MM-DDTHH:mm'),
        mvygEndTime: moment(item.mvygEndTime).format('YYYY-MM-DDTHH:mm'),
        originDisplayName: origin ? origin.displayName : '',
        destinationDisplayName: destination ? destination.displayName : '',
      };
      this.displayForm = true;
    },
    onCancel() {
      this.displayForm = false;
      this.errors = {};
    },
    async onSubmit() {
      this.isSubmit = true;
      this.errors = {};
      if (moment(this.currentItem.mvygStartTime).isAfter(this.currentItem.mvygEndTime)) {
        this.errors.mvygStartTime = 'Start Time cannot be after End Time.';
        this.errors.mvygEndTime = 'Start Time cannot be after End Time.';
        this.$toast.error('Please check the form for errors.');
        this.isSubmit = false;
        return;
      }
      if (!this.currentItem.mvygOriginLndmId) {
        this.errors.mvygOriginLndmId = 'Origin is required.';
        this.$toast.error('Please select an origin landmark.');
        this.isSubmit = false;
        return;
      }
      if (!this.currentItem.mvygDestinationLndmId) {
        this.errors.mvygDestinationLndmId = 'Destination is required.';
        this.$toast.error('Please select a destination landmark.');
        this.isSubmit = false;
        return;
      }
      try {
        const payload = {
          mvygName: this.currentItem.mvygName,
          mvygNumber: this.currentItem.mvygNumber,
          mvygStartTime: moment(this.currentItem.mvygStartTime).format('YYYY-MM-DD HH:mm:ss'),
          mvygEndTime: moment(this.currentItem.mvygEndTime).format('YYYY-MM-DD HH:mm:ss'),
          mvygOriginLndmId: this.currentItem.mvygOriginLndmId,
          mvygDestinationLndmId: this.currentItem.mvygDestinationLndmId,
          mvygCargoStatus: this.currentItem.mvygCargoStatus,
          mvygCargoLoadKg: this.currentItem.mvygCargoLoadKg,
          mvygCargoType: this.currentItem.mvygCargoType,
          mvygNote: this.currentItem.mvygNote,
          mvygMassId: this.currentItem.mvygMassId,
        };

        if (this.currentItem.mvygId) {
          await this.voyageMaster.update(this.currentItem.mvygId, payload);
          this.$toast.success('Successfully updated voyage');
        } else {
          await this.voyageMaster.create(payload);
          this.$toast.success('Successfully created voyage');
        }
        this.displayForm = false;
        this.getData();
      } catch (e) {
        if (e.response && e.response.data && e.response.data.status && e.response.data.status.message) {
          const messages = e.response.data.status.message;
          const newErrors = {};
          Object.keys(messages).forEach((field) => {
            newErrors[field] = Object.values(messages[field]).join(' ');
          });
          this.errors = newErrors;
          this.$toast.error('Please check the form for errors.');
        } else {
          this.$toast.error('Something went wrong');
        }
        console.error(e);
      } finally {
        this.isSubmit = false;
      }
    },
    onDelete(item) {
      this.selectedItem = item;
      this.displayDeletion = true;
    },
    async onConfirmDeletion() {
      try {
        await this.voyageMaster.delete(this.selectedItem.mvygId);
        this.$toast.success('Successfully deleted data');
        this.getData();
      } catch (e) {
        this.$toast.error('Failed to delete data');
        console.error(e);
      } finally {
        this.displayDeletion = false;
        this.selectedItem = null;
      }
    },
    onDetail(item) {
      this.$router.push({ name: 'VoyageDetail', params: { id: item.mvygId } });
    },

    openMapSelector(target) {
      this.landmarkSelectionTarget = target;
      this.displayMapModal = true;
      this.selectedMapLandmark = null;
    },

    async ensureLeafletLoaded() {
      if (typeof L !== 'undefined') return true;

      const existingScript = document.querySelector('script[src="/script/leaflet.js"]');
      if (existingScript) {
        await new Promise((resolve, reject) => {
          if (typeof L !== 'undefined') {
            resolve();
            return;
          }
          existingScript.addEventListener('load', resolve, { once: true });
          existingScript.addEventListener('error', reject, { once: true });
        });
        return typeof L !== 'undefined';
      }

      await new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = '/script/leaflet.js';
        script.onload = resolve;
        script.onerror = reject;
        document.head.appendChild(script);
      });

      return typeof L !== 'undefined';
    },

    initLandmarkMap() {
      const mapContainer = document.getElementById('landmark-selector-map');
      if (!mapContainer) return false;

      this.mapInstance = L.map(mapContainer, {
        zoomControl: true,
      }).setView([-5.95, 106.95], 10);
      // eslint-disable-next-line
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 19,
        attribution: '&copy; OpenStreetMap contributors',
      }).addTo(this.mapInstance);

      this.mapInstance.on('moveend', () => {
        this.fetchMapLandmarks();
      });

      this.fetchMapLandmarks();
      return true;
    },

    async fetchMapLandmarks() {
      if (!this.mapInstance) return;
      this.mapLoading = true;
      try {
        const bounds = this.mapInstance.getBounds();
        const { data } = await LandmarkSvc.landmarks.area({
          minLatitude: bounds.getSouth(),
          maxLatitude: bounds.getNorth(),
          minLongitude: bounds.getWest(),
          maxLongitude: bounds.getEast(),
        });
        this.mapLandmarks = data.data || [];
        this.updateMapMarkers();
      } catch (e) {
        console.error('Failed to fetch landmarks for map area', e);
        this.$toast.error('Failed to load landmarks for the visible map area.');
      } finally {
        this.mapLoading = false;
      }
    },

    updateMapMarkers() {
      if (!this.mapInstance) return;

      if (this.markerLayer) {
        this.markerLayer.clearLayers();
      } else {
        // eslint-disable-next-line
        this.markerLayer = L.layerGroup().addTo(this.mapInstance);
      }

      // eslint-disable-next-line
      const defaultIcon = L.icon({
        iconUrl: '/marker/pinpoint.png',
        iconSize: [32, 32],
        iconAnchor: [16, 32],
        popupAnchor: [0, -32],
      });

      // eslint-disable-next-line
      const selectedIcon = L.icon({
        iconUrl: '/marker/online-marker.png',
        iconSize: [32, 32],
        iconAnchor: [16, 32],
        popupAnchor: [0, -32],
      });

      this.mapLandmarks.forEach((landmark) => {
        const lat = parseFloat(landmark.lndmLatitude);
        const lng = parseFloat(landmark.lndmLongitude);
        if (Number.isNaN(lat) || Number.isNaN(lng)) return;

        // eslint-disable-next-line
        const marker = L.marker([lat, lng], {
          icon: defaultIcon,
          landmarkId: landmark.lndmId,
        });

        marker.on('click', () => {
          this.selectedMapLandmark = landmark;
          this.markerLayer.eachLayer((layer) => layer.setIcon(defaultIcon));
          marker.setIcon(selectedIcon);
        });

        marker.bindPopup(`<b>${landmark.lndmName}</b><br>${landmark.lndmDescription || ''}`);

        this.markerLayer.addLayer(marker);
      });
    },

    onLandmarkSelectedFromMap() {
      if (!this.selectedMapLandmark) return;

      const { lndmId, lndmName, lndmCompId } = this.selectedMapLandmark;

      const activeCompId = this.$store.getters['auth/activeCompany'].compId;
      const isCompany = lndmCompId === activeCompId;

      const displayName = isCompany ? `[COMPANY] ${lndmName}` : `[GLOBAL] ${lndmName}`;

      if (this.landmarkSelectionTarget === 'origin') {
        this.$set(this.currentItem, 'mvygOriginLndmId', lndmId);
        this.$set(this.currentItem, 'originDisplayName', displayName);
        // Force update the search query in the child component
        this.$nextTick(() => {
          if (this.$refs.originInput) {
            this.$refs.originInput.searchQuery = displayName;
          }
        });
      } else if (this.landmarkSelectionTarget === 'destination') {
        this.$set(this.currentItem, 'mvygDestinationLndmId', lndmId);
        this.$set(this.currentItem, 'destinationDisplayName', displayName);
        // Force update the search query in the child component
        this.$nextTick(() => {
          if (this.$refs.destinationInput) {
            this.$refs.destinationInput.searchQuery = displayName;
          }
        });
      }

      this.displayMapModal = false;
      this.selectedMapLandmark = null;
    },

    loadLeafletCSS() {
      if (!document.querySelector('link[href="/style/leaflet.css"]')) {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = '/style/leaflet.css';
        document.head.appendChild(link);
      }
    },

    async onMapModalShown() {
      if (this.mapInitInProgress) return;
      this.mapInitInProgress = true;

      try {
        const leafletReady = await this.ensureLeafletLoaded();
        if (!leafletReady) {
          this.$toast.error('Leaflet library is not loaded.');
          return;
        }

        this.$nextTick(() => {
          setTimeout(() => {
            this.destroyLandmarkMap();
            const initialized = this.initLandmarkMap();
            if (!initialized || !this.mapInstance) {
              this.$toast.error('Map container is not ready.');
              return;
            }

            this.mapInstance.invalidateSize();
            setTimeout(() => {
              if (!this.mapInstance) return;
              this.mapInstance.invalidateSize();
              this.fetchMapLandmarks();
            }, 250);
          }, 180);
        });
      } catch (e) {
        console.error('Failed to initialize map modal', e);
        this.$toast.error('Failed to initialize map.');
      } finally {
        this.mapInitInProgress = false;
      }
    },

    onMapModalHidden() {
      this.destroyLandmarkMap();
      this.selectedMapLandmark = null;
    },

    destroyLandmarkMap() {
      if (this.mapInstance) {
        this.mapInstance.off();
        this.mapInstance.remove();
        this.mapInstance = null;
      }
      this.markerLayer = null;
      this.mapLandmarks = [];
    },
  },
  watch: {
    selectedAsset(newVal) {
      // Only trigger getData if assets are already loaded (user selection)
      // During initial load, getData is called directly in getAssets()
      if (newVal && this.assets.length > 0 && this.assetsLoaded) {
        this.pagination.current = 1;
        this.getData();
      }
    },
    // eslint-disable-next-line func-names
    'currentItem.mvygCargoStatus': function (newVal) {
      // Set Cargo Type to '-' when No Cargo is selected
      // Clear Cargo Type when With Cargo is selected
      if (newVal === 0) {
        this.$set(this.currentItem, 'mvygCargoType', '-');
      } else if (newVal === 1) {
        this.$set(this.currentItem, 'mvygCargoType', '');
      }
    },
  },
  mounted() {
    this.loadLeafletCSS();
    this.getAssets();
    this.getLandmarks();
  },
  beforeDestroy() {
    this.destroyLandmarkMap();
  },
};
</script>

<style scoped>
.landmark-selector-map {
  width: 100%;
  height: 60vh;
  min-height: 420px;
}
</style>
